<script lang="ts">
import type { InputDateProps } from '@nuxt/ui';
import { PRIMITIVE_TYPE_DATE } from '#layers/data-model';

export const supportedDateFieldTypes = ({ primitiveType }: { primitiveType: PrimitiveType }) =>
	primitiveType.name === PRIMITIVE_TYPE_DATE ? ['datetime'] : [];

const __DsBlock = {
	icon: 'lucide:calendar',
	name: '$t:blocks.input_date.name',
	description: '$t:blocks.input_date.description',
	type: 'input',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: {
		supportedTypes: supportedDateFieldTypes,
		recommendedDisplays: ['$builtin:DisplayFormattedDate'],
	},

	options: [
		{
			name: 'modelValue',
			model: true,
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'disabled',
		},
		{
			name: 'readonly',
		},
		{
			name: 'disabledNotice',
		},
	],
}</script>

<script lang="ts" setup>
import type { Temporal } from 'temporal-polyfill';
import type { PrimitiveType } from '#layers/data-model';
import type { InputBlockProps } from '~/types/input-block';
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { calendarDateConverter, nowDate } from '#layers/base';
import { defineBlock } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { useDisabledNotice } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';

interface Props extends InputBlockProps, Pick<InputDateProps, 'color' | 'variant' | 'size' | 'disabled'> {
	block?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	block: true,
});

const model = defineModel<Temporal.PlainDate | null>();

;

const forwarded = reactivePick(props, 'color', 'variant', 'size', 'disabled', 'block', 'readonly');

const { modelValue: m, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const calendarModel = computed({
	get: () => calendarDateConverter.toCalendar(m.value),
	set: (value) => {
		m.value = calendarDateConverter.toTemporal(value);
	},
});

const setToday = () => {
	m.value = nowDate();
};
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInputDate
			v-bind="forwarded"
			v-model="calendarModel"
			:aria-readonly="readonly"
		>
			<template #trailing>
				<DPopover
					:content="{ side: 'bottom', align: 'end' }"
					:ui="{
						content: 'p-2 text-center',
					}"
				>
					<DButton
						v-if="!readonly"
						color="neutral"
						variant="link"
						data-test-id="input-date-calendar"
						icon="lucide:calendar"
						:disabled
					/>

					<template #content>
						<DCalendar v-model="calendarModel" />
						<DSeparator class="my-2" />
						<DButton
							v-if="!readonly"
							variant="link"
							data-test-id="input-date-now"
							@click="setToday"
						>
							{{ $t('blocks.input_date.now') }}
						</DButton>
					</template>
				</DPopover>
			</template>
		</DInputDate>
	</DTooltip>
</template>
